import { memo, useCallback, useMemo, useRef, useState } from 'react';

import getVideoId from 'get-video-id';
import { FaChevronLeft, FaChevronRight, FaPlay } from 'react-icons/fa';

import { VideoData } from '@common/types/api';

import * as S from './styles';

type SponsorVideosProps = {
  videos: VideoData[];
  color: string;
};

const FALLBACK_THUMBNAIL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAQMAAADCCAMAAAB6zFdcAAAAS1BMVEXN7CSE3Ob+UiJBNjLp8fT1/IoOrNO9LQh92+XX7PLo9fjxvLT/RwP/QBX284X7/oWi18IAqdQclLNDLCE3NzO0LQ29IgbDhRXO8iWCnLkgAAABEklEQVR4nO3QRQGAABAAMNzd+yelwfHks0VYksaatgh1/ZCFxqkMzctahbY9Dx1nHbruJ/ngwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwIEDBw4cOHDgwMHPBy+NyEugMEx2EAAAAABJRU5ErkJggg==';

const MIN_VIDEOS_FOR_ARROWS = 5;

const getVideoThumbnail = (urlVideo: string) => {
  const { id, service } = getVideoId(urlVideo);
  if (service === 'youtube' && id) {
    return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
  }
  return FALLBACK_THUMBNAIL;
};

const SponsorVideos = ({ videos, color }: SponsorVideosProps) => {
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const currentVideo = useMemo(() => {
    const video = videos[selectedVideoIndex];
    const { id, service } = getVideoId(video.url_video);

    let src = video.url_video;

    if (service === 'youtube') {
      src = `https://www.youtube.com/embed/${id}`;
    } else if (service === 'vimeo') {
      src = `https://player.vimeo.com/video/${id}`;
    }

    return { src, description: video.description };
  }, [selectedVideoIndex, videos]);

  const handleSelectVideo = (index: number) => () =>
    setSelectedVideoIndex(index);

  const scrollCarousel = useCallback((dir: number) => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: dir * 220, behavior: 'smooth' });
    }
  }, []);

  const showArrows = videos.length > MIN_VIDEOS_FOR_ARROWS;

  return (
    <S.Container>
      <S.PlayerWrapper>
        <iframe
          title={currentVideo.description}
          src={currentVideo.src}
          frameBorder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </S.PlayerWrapper>

      {videos.length > 1 && (
        <S.CarouselWrapper>
          {showArrows && (
            <S.CarouselArrow
              type="button"
              $direction="left"
              onClick={() => scrollCarousel(-1)}
            >
              <FaChevronLeft size={14} />
            </S.CarouselArrow>
          )}

          {showArrows && (
            <S.CarouselArrow
              type="button"
              $direction="right"
              onClick={() => scrollCarousel(1)}
            >
              <FaChevronRight size={14} />
            </S.CarouselArrow>
          )}

          <S.CarouselScroll ref={carouselRef}>
            {videos.map((video, index) => {
              const isActive = selectedVideoIndex === index;
              const thumbnail = getVideoThumbnail(video.url_video);

              return (
                <S.VideoThumbnail
                  key={`${video.url_video}-${video.description}`}
                  type="button"
                  onClick={handleSelectVideo(index)}
                  $active={isActive}
                  $color={color}
                >
                  <S.ThumbnailImage src={thumbnail} alt={video.description} />
                  <S.ThumbnailOverlay>
                    <S.ThumbnailPlayIcon $active={isActive} $color={color}>
                      <FaPlay
                        size={10}
                        color={isActive ? color : 'rgba(255, 255, 255, 0.7)'}
                      />
                    </S.ThumbnailPlayIcon>
                    <S.ThumbnailInfo $active={isActive}>
                      <div>{video.description}</div>
                    </S.ThumbnailInfo>
                  </S.ThumbnailOverlay>
                  {isActive && <S.ActiveIndicator $color={color} />}
                </S.VideoThumbnail>
              );
            })}
          </S.CarouselScroll>
        </S.CarouselWrapper>
      )}
    </S.Container>
  );
};

export default memo(SponsorVideos);
